import { apiErrorMessage } from '@fieldvision/web-core';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, LoaderCircle, Search } from 'lucide-react';

import type { ComboboxOption } from './combobox.logic.js';
import { truncationNotice } from './combobox.logic.js';
import { useDialogPortalContainer } from './Dialog.js';
import { pinnedRows, selectionSummary, toggleOption } from './multi-select.logic.js';
import { CONTROL_SHELL, Spinner, useFieldInvalid } from './primitives.js';

/**
 * `MultiSelect` for a list too long to hold — outlets, petugas, SKUs — which is
 * searched a page at a time like `Combobox`.
 *
 * CLOSED, IT READS LIKE `MultiSelect`: the "all" wording, the one chosen name, or
 * a count. OPEN, a search box sits above the list, and the list is every chosen
 * row first — see `pinnedRows` — then the results of the search. Each tick
 * applies at once and the list stays open, so picking five outlets is five
 * searches in one sitting rather than five trips.
 *
 * PRESENTATIONAL AND FULLY CONTROLLED, the split `Combobox` makes: the wrappers
 * in `ReferenceMultiPicker.tsx` own the search text, the debounce and the query.
 *
 * THE VALUE IS OPTIONS, NOT IDS — see `toggleOption`. The results change with
 * every search, so the selection has to carry the labels it is shown with.
 */
export function SearchMultiSelect({
  value,
  onChange,
  query,
  onQueryChange,
  open,
  onOpenChange,
  options,
  total,
  loading,
  error,
  allLabel,
  noun,
  searchPlaceholder,
  emptyLabel,
  disabled = false,
}: {
  value: readonly ComboboxOption[];
  onChange: (value: ComboboxOption[]) => void;
  query: string;
  onQueryChange: (query: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** This page of search results. */
  options: readonly ComboboxOption[];
  /** How many rows MATCHED, not how many arrived — this drives the footer. */
  total: number;
  loading: boolean;
  /** Rendered in the list. A failed request must not read as "no matches". */
  error?: unknown;
  /** The empty selection's wording, and the row that clears — "Semua Outlet". */
  allLabel: string;
  /** What the count counts — "Outlet" gives "2 Outlet dipilih". */
  noun: string;
  searchPlaceholder: string;
  emptyLabel: (term: string) => string;
  disabled?: boolean;
}) {
  const container = useDialogPortalContainer();
  const invalid = useFieldInvalid();
  const chosen = new Set(value.map((option) => option.id));
  const rows = pinnedRows(options, value);
  const notice = truncationNotice(options.length, total);
  const isOpen = open && !disabled;

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={`focus-ring ${CONTROL_SHELL} flex items-center gap-2 px-3 text-left disabled:bg-panel disabled:text-muted`}
        >
          <span className="min-w-0 flex-1 truncate">
            {selectionSummary(value, value.map((option) => option.id), allLabel, noun)}
          </span>
          <ChevronDown size={16} className="shrink-0 text-steel" aria-hidden />
        </button>
      </Popover.Trigger>

      <Popover.Portal container={container}>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          // Wider than a grid column when it can be: outlet and SKU names carry a
          // second line, and a picker the width of "Semua Outlet" truncates both.
          className="surface z-50 w-[max(var(--radix-popover-trigger-width),18rem)] max-w-[var(--radix-popover-content-available-width)] overflow-hidden"
        >
          <div className="flex items-center gap-1.5 border-b border-line px-3">
            <Search size={16} className="shrink-0 text-steel" aria-hidden />
            <input
              // The box the reader opened this for; the list is below it.
              autoFocus
              aria-label={searchPlaceholder}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="search"
              enterKeyHint="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              className="h-10 min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-muted"
            />
            {loading ? (
              <LoaderCircle size={16} className="shrink-0 animate-spin text-primary-700" aria-hidden />
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => onChange([])}
            className="tap-target flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-tint"
          >
            <Check
              size={16}
              className={`shrink-0 ${value.length === 0 ? 'text-primary-700' : 'invisible'}`}
              aria-hidden
            />
            {allLabel}
          </button>

          <ul
            aria-label={noun}
            className="max-h-[min(18rem,var(--radix-popover-content-available-height))] overflow-y-auto border-t border-line py-1"
          >
            {error ? (
              <li className="px-3 py-2 text-sm text-danger">{apiErrorMessage(error)}</li>
            ) : null}
            {!error && loading && rows.length === 0 ? (
              <li className="px-3 py-2">
                <Spinner label="Memuat…" />
              </li>
            ) : null}
            {!error && !loading && rows.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted">{emptyLabel(query.trim())}</li>
            ) : null}

            {rows.map((option) => (
              <li key={option.id}>
                <label className="tap-target flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-tint">
                  <input
                    type="checkbox"
                    checked={chosen.has(option.id)}
                    onChange={() => onChange(toggleOption(value, option))}
                    className="h-4 w-4 shrink-0 accent-primary-700"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{option.label}</span>
                    {option.hint ? (
                      <span className="block truncate text-xs text-muted">{option.hint}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {notice ? (
            <p className="border-t border-line px-3 py-2 text-xs text-muted">{notice}</p>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
